
import { HttpResponse } from "@angular/common/http";
import { inject, Injectable, Injector } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Observable, of as observableOf } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { StateService } from "../@core/utils";
import { nbOptionsFactory, nbStrategiesFactory, nbTokensFactory } from "../authentication/auth.module";
import { NbAuthOptions } from "../authentication/auth.options";
import { NbAuthSimpleToken, NbAuthToken, NbAuthTokenClass } from "../authentication/services/token/token";
import { NbTokenPack } from "../authentication/services/token/token-parceler";
import { getDeepFromObject, NbPasswordAuthStrategy } from "../authentication/strategies/password/password-strategy";
import { NbPasswordAuthStrategyOptions } from "../authentication/strategies/password/password-strategy-options";
import { NavigationHelpers } from "../common/utils";

import { ApiService } from "./api/api.service";
import { loginPagePath } from "./pagePaths";
import { frontendRoleType } from "../interfaces/apiCalls";
import { UserDetails } from "@med_poc/types/";
import { homePath, navigateToLoginAndRedirectAfter } from "../../hh_platform_frontend/docs/app/app.paths";


// Can be simply put in the canActivate array of a route, e.g. canActivate: [IsAuthenticated]
// Fetches the roles which are definied as professional in the backend, compares it to yours and allows access if you are a professional.
export const allowOnlyProfessionals = async (route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot) => { 
      

      const apiServiceRef = inject(ApiService);
      const stateServiceRef = inject(StateService);
      const router = inject(Router);
      let professionalRoles
      
      // hint for debugging issues:
      // routerState.url 

      // reduce backend calls by checking if the professionalRoles are already fetched and stored in the stateServiceRef. If not, fetch them from the backend.
      try{
        if(stateServiceRef.professionalRoles.length == 0){
          professionalRoles = await apiServiceRef.getAllProfessionalRoles();
          stateServiceRef.professionalRoles = professionalRoles;
        } else {
          professionalRoles = stateServiceRef.professionalRoles;
        }
      }
      catch(error){
        // if the API call failed, we get here, the user is not logged in.

        navigateToLoginAndRedirectAfter(router, routerState.url as `/${string}`);
        return false
      }

      return await _requireRoles(professionalRoles as frontendRoleType[], routerState, apiServiceRef, router)
    }

// Guard factory, use it directly in the route config
export const requireRoles = (roles: frontendRoleType[]) => {
  return async (activatedRouteSnapshot: ActivatedRouteSnapshot, routerStateSnapshot: RouterStateSnapshot) => {
    // inject() must run before the first await, otherwise we lose the injection context
    const apiServiceRef = inject(ApiService);
    const router = inject(Router);
    return await _requireRoles(roles, routerStateSnapshot, apiServiceRef, router)
  }
}

const _requireRoles = async (roles: frontendRoleType[], routerState: RouterStateSnapshot, apiServiceInstance: ApiService, router: Router) => {
  const attemptedUrl = routerState?.url;
  var type: frontendRoleType;

  // if there are no roles passed, we assume it is because the fetching of some roles from
  // the backend was not successful, A.k.a. the user is not logged in or sort.
  if (!roles){
    navigateToLoginAndRedirectAfter(router, attemptedUrl as `/${string}`);
    return false;
  }

  try{
    console.devlog("_requireRoles: checking for roles ", roles);
    let res = await apiServiceInstance.getMyRoleAsync();
    console.devlog('2', res);
    type = res.type;
  } catch (error) {
    console.deverror('Error fetching user role:', error);
    navigateToLoginAndRedirectAfter(router, attemptedUrl as `/${string}`);
    return false;
  }

  if (!roles.includes(type)){
    router.navigate([homePath()]);
  }

  return roles.includes(type)
}

/**
 * Checks if the current user has a specific user.details set  (doctor, pateint, etc..). 
 * We use the strapi component's name as an ID here, e.g. user-data.doctor-user
 *
 * @param detailComponent - The strapi component that represents this user.detail, see strapi components.
 * @returns A promise that resolves to true if the user has the specified detail, false otherwise
 * 
 * @example
 * ```typescript
 * const canAccess = await hasDetail('user-data.doctor-user');
 * if (canAccess) {
 *   // User has the user.details user-data.doctor-user defined.
 * }
 * ```
 */
export const hasDetail = async (detailComponent: UserDetails[keyof UserDetails]["__component"], redirectIfNotLoggedIn: `/${string}`) => {
  let details = await inject(ApiService).getMyDetails();
  if (details.some(detail => detail.__component === detailComponent)) {
    return true;
  } else {
    if (redirectIfNotLoggedIn) {


      // let us not rely on details, but roles only.


      

      //  PUT THE CURRENT ROUTE IN HERE. not the full url !


      // inject(Router).navigate([redirectIfNotLoggedIn, { queryParams: { redirectAfterLogin: route as ActivatedRouteSnapshot } }]);
    }
    return false;
  }

}

@Injectable({ providedIn: 'root' })
/* @Injectable() */
export class IsAuthenticated  {

  fallbackClass: NbAuthTokenClass = NbAuthSimpleToken;
  tokenClasses: NbAuthTokenClass[];

  constructor(private injector: Injector, public router: Router/* private tokenService: NbTokenService */) {
  
  const NB_AUTH_USER_OPTIONS : NbAuthOptions = {

    strategies: [
      NbPasswordAuthStrategy.setup({
        baseEndpoint: environment.apiUrl,
        name: 'email',
        login:  {
              endpoint: 'api/auth/local',
              method: 'post',
              requireValidToken: true,
              redirect: {
                success: '/',
                failure: null,
              },
        },
        token: {
              key: 'body.jwt', /* currently hardcoded in  strategies -> password -> authenticate*/
              getter: (module: string, res: HttpResponse<Object>, options: NbPasswordAuthStrategyOptions) => getDeepFromObject(
                res.body,
                options.token.key,
              ),
            },
       }),
    ],
  }



const NB_AUTH_OPTIONS = nbOptionsFactory(NB_AUTH_USER_OPTIONS);
const NB_AUTH_STRATEGIES = nbStrategiesFactory(NB_AUTH_OPTIONS, this.injector);
const NB_AUTH_TOKENS = nbTokensFactory(NB_AUTH_STRATEGIES);
this.tokenClasses = NB_AUTH_TOKENS;


    /* 

    { provide: NB_AUTH_TOKENS, useFactory: nbTokensFactory, deps: [NB_AUTH_STRATEGIES] },
    { provide: NB_AUTH_FALLBACK_TOKEN, useValue: NbAuthSimpleToken }, */
  
  
  }

/*   async canActivate(): boolean {
    let isAuthenticated = await this.auth
      .toPromise();

    // redundant for now, redirection to login page is done in 'isAuthenticatedOrRefresh' currently
    if (!isAuthenticated) {
        this.router.navigate([loginPagePath()]);
        return false;
    } else {
      return true;
    }
  } */


  /* canActivate(route:ActivatedRouteSnapshot, state:RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree { */
  canActivate(route:ActivatedRouteSnapshot, state:RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    var isAuthenticated;
    this.isAuthenticated().subscribe((res) => {
      isAuthenticated = res;
    });

    if (isAuthenticated) {
      return true;
    } else {
      this.router.navigate([loginPagePath()]);
      return false;
    }
  }

    /* auth utilities:  */

      /**
   * Returns true if auth token is present in the token storage
   * @returns {Observable<boolean>}
   */
  isAuthenticated(): Observable<boolean> {
    return this.getToken()
      .pipe(map((token: NbAuthToken) => token.isValid()));
  }

  getToken(): Observable<NbAuthToken> {
    return this.get();
  }

   /**
   * Returns observable of current token
   * @returns {Observable<NbAuthToken>}
   */
   get(): Observable<NbAuthToken> {
    const token = this.tokenStorageget();
    return observableOf(token);
  }

    /**
   * Returns token from localStorage
   * @returns {NbAuthToken}
   */
    tokenStorageget(): NbAuthToken {
      let key = 'auth_app_token';
      const raw = localStorage.getItem(key);
      return this.unwrap(raw);
    }

    unwrap(value: string): NbAuthToken {
      let tokenClass: NbAuthTokenClass = this.fallbackClass; 
      let tokenValue = '';
      let tokenOwnerStrategyName = '';
      let tokenCreatedAt: Date = null;
  
      const tokenPack: NbTokenPack = this.parseTokenPack(value);
      if (tokenPack) {
        tokenClass = this.getClassByName(tokenPack.name) || this.fallbackClass;
        tokenValue = tokenPack.value;
        tokenOwnerStrategyName = tokenPack.ownerStrategyName;
        tokenCreatedAt = new Date(Number(tokenPack.createdAt));
      }
  
      return this.nbAuthCreateToken(tokenClass, tokenValue, tokenOwnerStrategyName, tokenCreatedAt);
  
    }
     
    nbAuthCreateToken<T extends NbAuthToken>(tokenClass: NbAuthTokenClass<T>,
      token: any,
      ownerStrategyName: string,
      createdAt?: Date) {
        return new tokenClass(token, ownerStrategyName, createdAt);
    }

    protected parseTokenPack(value): NbTokenPack {
      try {
        return JSON.parse(value);
      } catch (e) { }
      return null;
    }

    protected getClassByName(name): NbAuthTokenClass {
      return this.tokenClasses.find((tokenClass: NbAuthTokenClass) => tokenClass.NAME === name);
    }

    /* 
  canLoad(route:ActivatedRouteSnapshot, state:RouterStateSnapshot):Observable<boolean>|boolean {
    return this.auth.isAuthenticatedOrRefresh()
    } */
}

@Injectable({ providedIn: 'root' })
export class ViewUserProfiles  {
 
  constructor(public router: Router, private apiService: ApiService, private state: StateService, private navigate: NavigationHelpers ) { }
   
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return new Promise<boolean>( async (resolve, reject) => {
    
    if (!this.state.user) {
        try {
          await this.apiService.setMe();
          resolve (['authenticated', 'assistant', 'administrator'].includes(this.apiService.getMyRole().type));
        } catch(err) {
          this.navigate.toLoginPage();
          return false
        }
    } resolve (['authenticated', 'assistant', 'administrator'].includes(this.apiService.getMyRole().type))


    });
  }
  
}